import { Event, EventEntry, EventRules, EventRulesAgreement } from "../../models"
import { getEventRulesByEventId } from "./EventRules"
import { getEventRulesAgreementsByEntryId } from "./EventRulesAgreement"

export interface FormattedEventRule {
    rule: EventRules // The rule itself
    isRequired: boolean // Whether the rule is required
    isCollapsed: boolean // Whether the rule is visible or not
    fullName: string // The full name of the person who has agreed to the rule
    isAgreed: boolean // Whether the checkbox is currently checked
    pastRuleAgreement: EventRulesAgreement | null | undefined // Record of past rule agreements for this entry
}

export interface FormattedWaiverData {
    id: string; // The id of the entry
    title: string; // The title of the row for this entry
    eventEntry: EventEntry; // The entry itself
    rules: FormattedEventRule[]; // The event rules and if they have been agreed to for this entry
}

export const verifyRuleInput = (fullName: string, isChecked: boolean) => { 
    let isValid = true;
    if (!fullName || fullName.trim() === "") {
        isValid = false;
    }
    if (!isChecked) {
        isValid = false;
    }
    return isValid;
}

export const getChipTitle = (waiversData: FormattedWaiverData[]) => {
    // Start with the following assumptions:
    let hasNoRules = true;
    let hasNotStarted = true;
    let hasRequiredRulesThatAreNotCompleted = false;
    
    // Determine if there are no completed entries, if so, return "Not Started".
    if (waiversData.length === 0) return "Not Started";

    // Determine if there are any rules that are not completed, if so, return "Incomplete".
    for (let i = 0; i < waiversData.length; i++) {
        const current = waiversData[i];
        const currentRules = current.rules;
        if (currentRules.length > 0) {
            hasNoRules = false;
            for (let j = 0; j < currentRules.length; j++) {
                const currentRule = currentRules[j];
                const isRuleInputValid = verifyRuleInput(currentRule.fullName, currentRule.isAgreed);
                if (currentRule.isRequired && !isRuleInputValid) {
                    hasRequiredRulesThatAreNotCompleted = true;
                } else if (currentRule.isRequired && isRuleInputValid) {
                    hasNotStarted = false;
                }
            }
        }
    }

    if (hasRequiredRulesThatAreNotCompleted) return "Incomplete";
    if (hasNoRules) return "Completed";
    if (hasNotStarted) return "Not Started";
    
    return "Completed";
}

export const formatEventRules = (eventRules: EventRules[], selectedPreviousAgreements?: EventRulesAgreement[]) => {
    let formattedRules: FormattedEventRule[] = [];
    for (var i = 0; i < eventRules.length; i++) {
        const currentRule = eventRules[i];
        let foundPreviousAgreement = false;
        let previousAgreement: EventRulesAgreement | null | undefined;
        if (selectedPreviousAgreements) {
            selectedPreviousAgreements.forEach((previous: EventRulesAgreement) => {
                if (previous.eventRule?.id === currentRule.id) {
                    foundPreviousAgreement = true;
                    previousAgreement = previous;
                }
            });
        }
        const formattedRule: FormattedEventRule = {
            isAgreed: foundPreviousAgreement,
            fullName: (previousAgreement && previousAgreement.name) ? previousAgreement.name : "",
            isRequired: currentRule.requiresAgreementPerEntry ? true : false,
            isCollapsed: true,
            rule: currentRule,
            pastRuleAgreement: previousAgreement || null
        }
        formattedRules.push(formattedRule);
    } 
    return formattedRules;
}

export const getFormattedWaiverData = async (event: Event, currentEventEntries: EventEntry[]) => {
    // First, get the rules for this event.
    let eventRules: EventRules[] = [];
    const rulesQueryResult = await getEventRulesByEventId(event.id);
    if (rulesQueryResult.isSuccess) {
        eventRules = rulesQueryResult.result;
    } else {
        console.error("Sorry, a problem occurred while trying to find the event's rules.");
    }

    // Next, loop through each entry and format the data.
    let formattedEntries: FormattedWaiverData[] = [];
    for (let i = 0; i < currentEventEntries.length; i++) {
        const current = currentEventEntries[i];

        // Get the previous rule agreements for this entry
        let previousRuleAgreements: EventRulesAgreement[] = [];
        const agreementsQueryResult = await getEventRulesAgreementsByEntryId(current.id);
        if (agreementsQueryResult.isSuccess) {
            previousRuleAgreements = agreementsQueryResult.result;
        } else {
            console.error("Sorry, a problem occurred while trying to find the past rule agreements.");
        }

        // Format the rules
        const formattedRules = formatEventRules(eventRules, previousRuleAgreements);

        const title = "Entry " + (i + 1).toString() + " - " + current.horseName;
        const formattedData: FormattedWaiverData = {
            id: current.id,
            title: title,
            eventEntry: current,
            rules: formattedRules || [],
        };
        formattedEntries.push(formattedData);
    }
    return formattedEntries;
};